import { useState } from "react";
import { Link, useRoute, useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import Icon from "@/components/mnf/Icon";
import { GlassCard, PillTag, Btn, Avatar } from "@/components/mnf/UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type SisterhoodPostKind,
  type SisterhoodReactionKind,
} from "@/lib/store";

/**
 * SisterhoodPost
 *
 * The detail view for a single post. Shows the full body, the three reaction
 * buttons with current counts, the comment thread, and a report flag for
 * sisters who see something that needs Coach Kiley's eye. Comments respect
 * the listening period and the same sensitive-keyword routing as posts.
 */

const KIND_META: Record<
  SisterhoodPostKind,
  { label: string; tone: "coral" | "purple" | "gold" | "forest" | "fiber"; emoji: string }
> = {
  testimony: { label: "Testimony", tone: "coral",  emoji: "✨" },
  win:       { label: "Win",       tone: "gold",   emoji: "🌾" },
  prayer:    { label: "Prayer",    tone: "purple", emoji: "🙏" },
  verse:     { label: "Verse",     tone: "forest", emoji: "📖" },
  question:  { label: "Question",  tone: "fiber",  emoji: "💬" },
};

const REACTION_META: Record<
  SisterhoodReactionKind,
  { label: string; emoji: string }
> = {
  amen:    { label: "Amen",    emoji: "🙌" },
  praying: { label: "Praying", emoji: "🙏" },
  "me-too":{ label: "Me too",  emoji: "💛" },
};

const REPORT_REASONS = [
  "Hurtful or unkind language",
  "Spam or self-promotion",
  "Sensitive content needs coach review",
  "Something else",
];

export default function SisterhoodPostPage() {
  const [, params] = useRoute<{ id: string }>("/sisterhood/post/:id");
  const [, setLocation] = useLocation();
  const me = useStoreQuery(() => queries.currentUser());
  // No fallback to the seeded demo account: reacting, commenting, and reporting below
  // all WRITE with this id. See the early return below, right after the
  // existing `if (!post)` guard.
  const userId = me?.id ?? null;
  const post = useStoreQuery(() => (params?.id ? queries.sisterhoodPostById(params.id) : null));
  const author = useStoreQuery(() => (post ? queries.userById(post.authorId) : null));
  const comments = useStoreQuery(() => (post ? queries.sisterhoodCommentsForPost(post.id) : []));
  const reactions = useStoreQuery(() => (post ? queries.sisterhoodReactionsForPost(post.id) : null));
  const myReaction = useStoreQuery(() =>
    post && userId ? queries.mySisterhoodReactionForPost(userId, post.id) : null,
  );
  const inListening = useStoreQuery(() => (userId ? queries.isInListeningPeriod(userId) : true));
  const toggleReaction = useStoreMutation(mutations.togglePostReaction);
  const addComment = useStoreMutation(mutations.addPostComment);
  const reportPost = useStoreMutation(mutations.reportPost);

  const [draft, setDraft] = useState("");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportFiled, setReportFiled] = useState(false);

  if (!post) {
    return (
      <>
        <ScreenHeader variant="forest" eyebrow="The Sisterhood" title="Post not found" />
        <div className="px-5 pt-6 pb-28">
          <GlassCard padding={20}>
            <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", textAlign: "center" }}>
              This post may have been removed by Coach Kiley or the sister.
            </p>
            <Btn variant="secondary" block size="md" onClick={() => setLocation("/sisterhood")} className="mt-4" data-testid="button-back-to-feed">
              Back to the feed
            </Btn>
          </GlassCard>
        </div>
      </>
    );
  }

  if (!userId) {
    return (
      <div className="px-5 pt-6">
        <p className="mnf-body" style={{ color: "var(--mnf-tg)" }}>
          Sign in to react and comment, sister.
        </p>
      </div>
    );
  }

  const meta = KIND_META[post.kind];
  const minutes = Math.max(1, Math.floor((Date.now() - post.createdAt) / 60000));
  const ago =
    minutes < 60 ? `${minutes}m ago`
    : minutes < 60 * 24 ? `${Math.floor(minutes / 60)}h ago`
    : `${Math.floor(minutes / (60 * 24))}d ago`;

  const onComment = () => {
    if (!draft.trim() || inListening) return;
    addComment({ userId, postId: post.id, body: draft });
    setDraft("");
  };

  return (
    <>
      <ScreenHeader
        variant="forest"
        eyebrow="The Sisterhood"
        title="A sister's voice"
        trailing={
          <Link
            href="/sisterhood"
            className="no-underline flex items-center justify-center hover-elevate"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "rgba(245,240,232,0.12)", color: "#F5F0E8",
              border: "1px solid rgba(245,240,232,0.18)",
            }}
            aria-label="Back to feed"
            data-testid="link-back-feed"
          >
            <Icon name="arrow_back" size={18} />
          </Link>
        }
      />

      <div className="px-5 pt-4 pb-32 space-y-4" style={{ background: "var(--mnf-cream)" }}>
        <GlassCard padding={20}>
          <div className="flex items-start gap-3 mb-3">
            <Avatar
              initials={author?.initials ?? "S"}
              src={author?.avatarDataUrl ?? undefined}
              size={48}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="mnf-serif" style={{ fontSize: 16, fontWeight: 500, color: "var(--mnf-fd)" }}>
                  {author?.name ?? "A sister"}
                </p>
                <PillTag tone={meta.tone}>
                  <span style={{ fontSize: 11 }}>{meta.emoji}</span>
                  {meta.label}
                </PillTag>
              </div>
              <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 2 }}>
                {ago}
              </p>
            </div>
            <button
              onClick={() => setReportOpen(true)}
              data-testid="button-open-report"
              aria-label="Report this post"
              className="hover-elevate"
              style={{
                width: 32, height: 32, borderRadius: 999,
                background: "rgba(30,74,53,0.06)", color: "var(--mnf-tg)",
                border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Icon name="flag" size={14} />
            </button>
          </div>

          {post.kind === "verse" && post.verseRef && (
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-f)", marginBottom: 6 }}>
              {post.verseRef}
            </p>
          )}
          <p
            className={post.kind === "verse" ? "mnf-serif italic" : "mnf-body"}
            style={{
              fontSize: post.kind === "verse" ? 18 : 15,
              color: "var(--mnf-fd)",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {post.body}
          </p>

          {post.photoDataUrl && (
            <img
              src={post.photoDataUrl}
              alt=""
              style={{
                width: "100%", borderRadius: 14, marginTop: 14,
                border: "1px solid rgba(30,74,53,0.08)",
              }}
            />
          )}

          <div className="flex items-center gap-2 mt-5 flex-wrap">
            {(["amen", "praying", "me-too"] as SisterhoodReactionKind[]).map((kind) => {
              const rmeta = REACTION_META[kind];
              const active = myReaction === kind;
              const count =
                kind === "amen" ? reactions?.amenLabel ?? "0"
                : kind === "praying" ? reactions?.prayingLabel ?? "0"
                : reactions?.meTooLabel ?? "0";
              const num =
                kind === "amen" ? reactions?.amen ?? 0
                : kind === "praying" ? reactions?.praying ?? 0
                : reactions?.meToo ?? 0;
              return (
                <button
                  key={kind}
                  onClick={() => toggleReaction({ userId, postId: post.id, kind })}
                  data-testid={`button-react-${kind}-${post.id}`}
                  className="inline-flex items-center gap-1.5 hover-elevate active-elevate-2"
                  style={{
                    padding: "8px 14px",
                    borderRadius: 999,
                    background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.06)",
                    color: active ? "#F5F0E8" : "var(--mnf-f)",
                    border: active ? "1px solid var(--mnf-f)" : "1px solid transparent",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 15 }}>{rmeta.emoji}</span>
                  <span>{rmeta.label}</span>
                  {num > 0 && (
                    <span style={{ opacity: active ? 0.85 : 0.7, marginLeft: 2 }}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </GlassCard>

        <p className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>
          Comments {comments.length > 0 && `· ${comments.length}`}
        </p>

        {comments.length === 0 && (
          <GlassCard padding={16}>
            <p className="mnf-body italic" style={{ fontSize: 12.5, color: "var(--mnf-tg)", textAlign: "center" }}>
              No comments yet. Your kind word would mean a lot.
            </p>
          </GlassCard>
        )}

        {comments.map((c) => (
          <CommentRow key={c.id} comment={c} />
        ))}

        {!inListening ? (
          <GlassCard padding={14}>
            <textarea
              data-testid="input-comment-body"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write a kind word..."
              rows={3}
              className="w-full mnf-body outline-none bg-transparent resize-none"
              style={{
                fontSize: 14, color: "var(--mnf-fd)", lineHeight: 1.5,
              }}
            />
            <div className="flex justify-end mt-2">
              <Btn
                variant="primary"
                size="sm"
                onClick={onComment}
                disabled={!draft.trim()}
                data-testid="button-submit-comment"
              >
                Send <Icon name="send" size={14} />
              </Btn>
            </div>
          </GlassCard>
        ) : (
          <GlassCard padding={14}>
            <p className="mnf-body italic" style={{ fontSize: 12.5, color: "var(--mnf-tg)", textAlign: "center" }}>
              In your listening week. Reading and reacting are open, comments unlock soon.
            </p>
          </GlassCard>
        )}
      </div>

      {reportOpen && (
        <ReportSheet
          onClose={() => { setReportOpen(false); setReportFiled(false); }}
          onSubmit={(reason) => {
            reportPost({ reporterId: userId, postId: post.id, reason });
            setReportFiled(true);
          }}
          filed={reportFiled}
        />
      )}
    </>
  );
}

/* ------------------------------- Comment ------------------------------- */

function CommentRow({ comment }: { comment: { id: string; userId: string; body: string; createdAt: number } }) {
  const author = useStoreQuery(() => queries.userById(comment.userId));
  const minutes = Math.max(1, Math.floor((Date.now() - comment.createdAt) / 60000));
  const ago =
    minutes < 60 ? `${minutes}m`
    : minutes < 60 * 24 ? `${Math.floor(minutes / 60)}h`
    : `${Math.floor(minutes / (60 * 24))}d`;

  return (
    <div className="flex items-start gap-3" data-testid={`comment-${comment.id}`}>
      <Avatar
        initials={author?.initials ?? "S"}
        src={author?.avatarDataUrl ?? undefined}
        size={32}
      />
      <div
        className="flex-1"
        style={{
          background: "rgba(255,255,255,0.65)",
          border: "1px solid rgba(30,74,53,0.06)",
          borderRadius: 14,
          padding: "10px 14px",
        }}
      >
        <div className="flex items-baseline gap-2">
          <p className="mnf-serif" style={{ fontSize: 13, fontWeight: 500, color: "var(--mnf-fd)" }}>
            {author?.name ?? "A sister"}
          </p>
          <p className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-tg)" }}>
            {ago}
          </p>
        </div>
        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-fd)", lineHeight: 1.5, marginTop: 2 }}>
          {comment.body}
        </p>
      </div>
    </div>
  );
}

/* ------------------------------- Report ------------------------------- */

function ReportSheet({
  onClose,
  onSubmit,
  filed,
}: {
  onClose: () => void;
  onSubmit: (reason: string) => void;
  filed: boolean;
}) {
  const [reason, setReason] = useState<string>("");

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="report-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        {filed ? (
          <div className="text-center py-4">
            <span style={{ fontSize: 36 }}>💛</span>
            <h2 className="mnf-serif mt-3" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)" }}>
              Thank you, sister
            </h2>
            <p className="mnf-body mt-2" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55 }}>
              Coach Kiley will read this within a day. Your name is private.
            </p>
            <Btn variant="primary" block size="lg" onClick={onClose} className="mt-5" data-testid="button-close-report-confirm">
              Close
            </Btn>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Send to Coach Kiley</p>
                <h2 className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
                  Flag this post
                </h2>
              </div>
              <button
                onClick={onClose}
                data-testid="button-close-report"
                className="hover-elevate"
                style={{
                  width: 36, height: 36, borderRadius: 999,
                  background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)",
                  border: "none", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Icon name="close" size={18} />
              </button>
            </div>

            <p className="mnf-body mb-3" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
              Coach Kiley will read this and take it from here. Your name stays private.
            </p>

            <div className="space-y-2">
              {REPORT_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  data-testid={`button-reason-${r.split(" ")[0].toLowerCase()}`}
                  className="w-full text-left hover-elevate active-elevate-2"
                  style={{
                    padding: "12px 14px",
                    borderRadius: 14,
                    background: reason === r ? "var(--mnf-f)" : "rgba(255,255,255,0.7)",
                    color: reason === r ? "#F5F0E8" : "var(--mnf-fd)",
                    border: `1px solid ${reason === r ? "var(--mnf-f)" : "rgba(30,74,53,0.06)"}`,
                    cursor: "pointer",
                    fontSize: 13.5,
                  }}
                >
                  {r}
                </button>
              ))}
            </div>

            <Btn
              variant="coral"
              block
              size="lg"
              onClick={() => reason && onSubmit(reason)}
              disabled={!reason}
              className="mt-4"
              data-testid="button-submit-report"
            >
              Send to Coach Kiley
            </Btn>
          </>
        )}
      </div>
    </div>
  );
}
